import { ContractError, IncentivesApiError, parseTransitionResponse, parseResponse } from './types'
import type { AnalyticsRangeKey, Endpoint, Metric, ResponseTypes, SectionKey } from './types'

export type ReadEndpoint = Exclude<Endpoint, 'invitations/attribution' | 'invitations/code/ensure' | 'notifications/seen' | 'invitations/redeem' | 'public/invitations/resolve' | 'public/invitations/open'>
type BaseQuery = { lang?: string }
type Pagination = { cursor?: string; page_size?: number }
export interface ReadQueries {
  initialize: BaseQuery
  overview: BaseQuery
  tasks: BaseQuery & Pagination & { section: SectionKey; period_id?: string; rule_id?: string; content_cursor?: string }
  records: BaseQuery & Pagination & { section?: SectionKey; period_id?: string; rule_id?: string; status?: 'pending' | 'granted' | 'rejected' | 'reversed'; receipt_ids?: string[] }
  notifications: BaseQuery & Pagination & { unseen_only?: boolean }
  'invitations/overview': BaseQuery
  'creator/analytics/overview': BaseQuery & { metric: Metric; range: AnalyticsRangeKey }
  'creator/analytics/contents': BaseQuery & Pagination & { metric: Metric; range: AnalyticsRangeKey; scope: 'cumulative' | 'range'; content_snapshot_id: string; content_id?: string }
}
export const readPaths: Record<ReadEndpoint, string> = {
  initialize: 'music/v1/incentives/initialize',
  overview: 'music/v1/incentives/overview', tasks: 'music/v1/incentives/tasks',
  records: 'music/v1/incentives/records', notifications: 'music/v1/incentives/notifications',
  'invitations/overview': 'music/v1/incentives/invitations/overview',
  'creator/analytics/overview': 'music/v1/creator/analytics/overview',
  'creator/analytics/contents': 'music/v1/creator/analytics/contents',
}
const allowed: Record<ReadEndpoint, string[]> = {
  initialize: ['lang'],
  overview: ['lang'], tasks: ['lang', 'section', 'period_id', 'rule_id', 'cursor', 'page_size', 'content_cursor'],
  records: ['lang', 'section', 'period_id', 'rule_id', 'status', 'receipt_ids', 'cursor', 'page_size'],
  notifications: ['lang', 'unseen_only', 'cursor', 'page_size'], 'invitations/overview': ['lang'],
  'creator/analytics/overview': ['lang', 'metric', 'range'],
  'creator/analytics/contents': ['lang', 'metric', 'range', 'scope', 'content_snapshot_id', 'content_id', 'cursor', 'page_size'],
}
export function requestBody<E extends ReadEndpoint>(endpoint: E, query: ReadQueries[E]): Record<string, unknown> {
  const invalid = () => { throw new IncentivesApiError(400, 'invalid_request') }
  if (!Object.hasOwn(allowed, endpoint) || !query || typeof query !== 'object' || Array.isArray(query)) return invalid()
  const body: Record<string, unknown> = { schema_version: 2 }
  for (const [key, value] of Object.entries(query)) {
    if (!allowed[endpoint].includes(key)) return invalid()
    if (value === undefined) continue
    if (key === 'receipt_ids') {
      if (!Array.isArray(value) || !value.length || value.length > 100 || new Set(value).size !== value.length || value.some(v => typeof v !== 'string' || !v || v.length > 128)) return invalid()
      body[key] = [...value]
    } else if (key === 'page_size') {
      if (!Number.isSafeInteger(value) || Number(value) < 1 || Number(value) > 100) return invalid()
      body[key] = value
    } else if (key === 'unseen_only') {
      if (typeof value !== 'boolean') return invalid()
      body[key] = value
    } else {
      if (typeof value !== 'string' || !value || value !== value.trim() || /[\u0000\r\n\t]/.test(value) || value.length > (key.includes('cursor') ? 8192 : key === 'content_snapshot_id' ? 256 : key === 'lang' ? 35 : 128)) return invalid()
      body[key] = value
    }
  }
  if (body.section && !['badge', 'creator_weekly', 'newcomer'].includes(body.section as string)) return invalid()
  if (body.status && !['pending', 'granted', 'rejected', 'reversed'].includes(body.status as string)) return invalid()
  if (endpoint === 'tasks' && (!body.section || body.cursor && body.content_cursor || body.content_cursor && !body.rule_id)) return invalid()
  if (body.receipt_ids && ['section', 'period_id', 'rule_id', 'status', 'cursor', 'page_size'].some(key => key in body)) return invalid()
  if (endpoint.startsWith('creator/analytics/')) {
    if (!['public_songs', 'plays', 'likes', 'remixes'].includes(body.metric as string) || !['last_7_days', 'last_30_days'].includes(body.range as string)) return invalid()
    if (endpoint.endsWith('/contents') && (!body.content_snapshot_id || !['cumulative', 'range'].includes(body.scope as string))) return invalid()
  }
  return body
}
/** Bind response semantics to the actual request; parsed schemas alone cannot prove the chosen filter. */
export function validateReadResult<E extends ReadEndpoint>(endpoint: E, query: ReadQueries[E], data: ResponseTypes[E]): void {
  const input = query as Record<string, unknown>, value = data as any
  const ensure = (valid: unknown) => { if (!valid) throw new ContractError(`${endpoint}.request_binding`) }
  if (endpoint === 'tasks') {
    ensure(value.section === input.section)
    if (input.period_id) ensure(value.period_id === input.period_id)
    if (input.rule_id) ensure(value.list.every((task: any) => task.rule_id === input.rule_id))
  }
  if (endpoint === 'records') {
    if (input.receipt_ids) {
      ensure(value.summary === null && !value.page.has_more)
      ensure(JSON.stringify(value.list.map((record: any) => record.id)) === JSON.stringify(input.receipt_ids))
    } else ensure(value.summary !== null)
  }
  if (endpoint.startsWith('creator/analytics/')) {
    ensure(value.range.key === input.range)
    if (endpoint.endsWith('/overview')) ensure(value.series.metric === input.metric)
    else {
      ensure(value.list.every((row: any) => Object.values(row.metrics).every((metric: any) => metric.scope === input.scope)))
      if (input.content_id) ensure(value.list.length === 1 && value.list[0].content_id === input.content_id)
    }
  }
}
export interface ReadTransport {
  post(path: string, body: Record<string, unknown>, config: {
    headers: Record<string, string>; signal: AbortSignal; musicmintInlineError: boolean; validateStatus: () => boolean
  }): Promise<{ status: number; data: unknown; headers?: Record<string, unknown> }>
}
export interface RequestContext { identity: string; generation: number; signal: AbortSignal }
export type ReadRequester = <E extends ReadEndpoint>(endpoint: E, query: ReadQueries[E], context: RequestContext) => Promise<ResponseTypes[E]>
/** Explicit calls only. A token resolved after logout/session replacement is never sent. */
export function createReadRequester(dependencies: {
  transport: ReadTransport
  token: () => Promise<string | null | undefined>
  current: (context: RequestContext) => boolean
}): ReadRequester {
  return async (endpoint, query, context) => {
    const body = requestBody(endpoint, query)
    const assertCurrent = () => { if (context.signal.aborted || !dependencies.current(context)) throw new IncentivesApiError(401, 'stale_session') }
    assertCurrent()
    const token = await dependencies.token()
    assertCurrent()
    if (!token) throw new IncentivesApiError(401, 'unauthorized')
    const negotiated = ['initialize', 'overview', 'tasks', 'records', 'notifications'].includes(endpoint)
    const post = () => dependencies.transport.post(readPaths[endpoint], body, {
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      signal: context.signal, musicmintInlineError: true, validateStatus: () => true,
    })
    let response = await post()
    // initialize is an explicit, idempotent write invoked by the rewards center.
    for (let attempt = 1; endpoint === 'initialize' && response.status === 202; attempt++) {
      assertCurrent()
      if ((response.data as any)?.code !== 202 || (response.data as any)?.data?.ready !== false) throw new ContractError('initialize.pending')
      if (attempt >= 4) throw new IncentivesApiError(503, 'initialization_timeout')
      const seconds = Number(response.headers?.['retry-after'] ?? 1)
      const delay = Math.min(5000, Math.max(1000, Number.isFinite(seconds) ? seconds * 1000 : 1000))
      await new Promise<void>((resolve, reject) => {
        const abort = () => { clearTimeout(timer); reject(new IncentivesApiError(401, 'stale_session')) }
        const timer = setTimeout(() => { context.signal.removeEventListener('abort', abort); resolve() }, delay)
        context.signal.addEventListener('abort', abort, { once: true })
      })
      assertCurrent(); response = await post()
    }
    assertCurrent()
    const data = (negotiated ? parseTransitionResponse : parseResponse)(endpoint, response.status, response.data, typeof response.headers?.['retry-after'] === 'string' ? response.headers['retry-after'] : null)
    validateReadResult(endpoint, query, data)
    return data
  }
}
